import React from 'react';
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaEnvelopeOpenText,
  FaChartBar,
  FaPlug,
  FaMobileAlt,
  FaLock,
  FaShieldAlt,
  FaCreditCard,
  FaUserShield,
  FaArrowRight,
  FaCheck,
  FaUserTie,
  FaBriefcase,
  FaCalculator,
} from 'react-icons/fa';
import usePageMeta from '../../hooks/usePageMeta';
import './NFSe.css';

const NFSE_URL =
  'https://nfe.updatecommit.com/?utm_source=updatecommit-site&utm_medium=produto&utm_campaign=nfse';
const NFSE_PLANOS_URL =
  'https://nfe.updatecommit.com/?utm_source=updatecommit-site&utm_medium=produto&utm_campaign=nfse#plans';
const NFSE_DOCS_URL = 'https://nfe.updatecommit.com/docs';

interface Audience {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface SecurityItem {
  icon: React.ReactNode;
  text: string;
}

interface Plan {
  name: string;
  price: string;
  target: string;
  highlight?: boolean;
}

interface Faq {
  question: string;
  answer: string;
}

const audiences: Audience[] = [
  {
    icon: <FaUserTie />,
    title: 'MEI e autônomos',
    description: 'Que perdem tempo no portal da prefeitura todo mês para emitir uma única nota.',
  },
  {
    icon: <FaBriefcase />,
    title: 'Empresas de serviço',
    description: 'Que faturam clientes recorrentes — mensalidades, contratos e projetos.',
  },
  {
    icon: <FaCalculator />,
    title: 'Contadores e escritórios',
    description: 'Que emitem e apuram para dezenas de CNPJs e precisam de um painel só.',
  },
];

const features: Feature[] = [
  {
    icon: <FaFileInvoiceDollar />,
    title: 'Emissão no padrão nacional',
    description:
      'Nota emitida e assinada digitalmente (certificado A1) direto na SEFAZ Nacional, com o DANFSe oficial em PDF e o XML para a contabilidade.',
  },
  {
    icon: <FaCalendarAlt />,
    title: 'Agendamento recorrente',
    description:
      'Configure uma vez ("todo dia 5, R$ 8.000 para o cliente X") e a nota é emitida e enviada sozinha, todo mês.',
  },
  {
    icon: <FaBuilding />,
    title: 'Multi-CNPJ',
    description:
      'Cada CNPJ emissor com seu certificado, série, regime tributário, PIX e até e-mail de envio próprios. Ideal para grupos e contadores.',
  },
  {
    icon: <FaEnvelopeOpenText />,
    title: 'Entrega automática por e-mail',
    description:
      'O cliente recebe a nota com PDF + XML anexos, QR Code PIX da cobrança e template com a sua marca.',
  },
  {
    icon: <FaChartBar />,
    title: 'Apuração mensal',
    description:
      'Todas as notas emitidas contra os seus CNPJs, importadas automaticamente da SEFAZ para fechar o mês sem planilha.',
  },
  {
    icon: <FaPlug />,
    title: 'API e Webhooks',
    description:
      'Integre com seu ERP: emissão, consulta, cancelamento e notificações em tempo real, com documentação completa.',
  },
  {
    icon: <FaMobileAlt />,
    title: 'No celular',
    description: 'Painel mobile-first: emita e consulte suas notas de qualquer lugar.',
  },
];

const securityItems: SecurityItem[] = [
  {
    icon: <FaLock />,
    text: 'Certificado digital e senhas criptografados com AES-256-GCM — nem a nossa equipe consegue lê-los.',
  },
  {
    icon: <FaShieldAlt />,
    text: 'Comunicação com a SEFAZ via mTLS + assinatura XMLDSIG.',
  },
  {
    icon: <FaCreditCard />,
    text: 'Pagamentos processados pela Stripe (PCI-DSS nível 1) — dados de cartão nunca passam pelos nossos servidores.',
  },
  {
    icon: <FaUserShield />,
    text: 'LGPD: seus dados são seus, com exportação e exclusão a qualquer momento. Infraestrutura 100% no Brasil.',
  },
];

const plans: Plan[] = [
  { name: 'Trial', price: 'grátis, 14 dias', target: 'Testar com o seu CNPJ real, sem cartão' },
  { name: 'MEI', price: 'R$ 29/mês', target: '10 notas/mês, alerta de desenquadramento' },
  { name: 'Starter', price: 'R$ 59/mês', target: '30 notas/mês, 1 CNPJ' },
  { name: 'Profissional', price: 'R$ 149/mês', target: 'Notas ilimitadas, 3 CNPJs, API', highlight: true },
  { name: 'Empresarial', price: 'R$ 349/mês', target: 'Ilimitado, 10 CNPJs, SLA, white-label' },
  { name: 'Contador', price: 'a partir de R$ 249/mês', target: '15 a 100 CNPJs monitorados, emissão ilimitada' },
];

const faqs: Faq[] = [
  {
    question: 'Funciona na minha cidade?',
    answer:
      'Sim, para todos os municípios conveniados ao padrão nacional NFS-e (Rio de Janeiro, São Paulo e mais de 1.000 cidades — a lista cresce todo mês).',
  },
  {
    question: 'Preciso de certificado digital?',
    answer:
      'Para emitir em produção, sim (A1). O upload é feito uma vez e o arquivo fica criptografado — ninguém consegue lê-lo, nem a nossa equipe.',
  },
  {
    question: 'E se eu já emito pelo portal nacional?',
    answer:
      'Suas notas anteriores aparecem na apuração automaticamente; a numeração continua de onde parou.',
  },
  {
    question: 'Tem suporte?',
    answer:
      'Central de Ajuda completa dentro da ferramenta + suporte por e-mail em todos os planos (prioritário a partir do Profissional).',
  },
];

const NFSe: React.FC = () => {
  usePageMeta({
    title: 'Emissor de NFS-e — Nota Fiscal de Serviço no piloto automático',
    description:
      'Emita, agende e envie suas Notas Fiscais de Serviço (NFS-e) no padrão nacional em segundos. DANFSe oficial, agendamento recorrente, multi-CNPJ e API. Teste 14 dias grátis, sem cartão.',
    path: '/solucoes/nfse',
  });

  return (
    <div className="nfse-page">
      {/* Hero */}
      <section className="nfse-hero">
        <span className="nfse-badge"><span aria-hidden="true">🧾</span> UpdateCommit NFS-e</span>
        <h1 className="nfse-title">
          Emita NFS-e em <span className="nfse-highlight">segundos</span>.
        </h1>
        <p className="nfse-subtitle">
          Sem contador de plantão, sem portal da prefeitura. A plataforma que emite, agenda e envia
          suas Notas Fiscais de Serviço no padrão nacional — direto do navegador ou do celular, com
          validade fiscal e o PDF oficial da prefeitura.
        </p>
        <div className="nfse-hero-buttons">
          <a
            href={NFSE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nfse-cta-primary"
          >
            Testar 14 dias grátis <FaArrowRight aria-hidden="true" />
          </a>
          <a href="#nfse-planos" className="nfse-cta-secondary">
            Ver planos
          </a>
        </div>
        <p className="nfse-risk-reversal">
          Sem cartão de crédito · cancele quando quiser · suporte humano
        </p>
      </section>

      {/* Para quem é */}
      <section className="nfse-section reveal">
        <h2 className="nfse-section-title">Para quem é</h2>
        <div className="nfse-audience-grid">
          {audiences.map((audience) => (
            <div key={audience.title} className="nfse-audience-card">
              <div className="nfse-audience-icon" aria-hidden="true">{audience.icon}</div>
              <h3>{audience.title}</h3>
              <p>{audience.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Recursos */}
      <section className="nfse-section nfse-section-alt">
        <h2 className="nfse-section-title">O que a plataforma faz</h2>
        <div className="nfse-features-grid">
          {features.map((feature) => (
            <div key={feature.title} className="nfse-feature-card">
              <div className="nfse-feature-icon" aria-hidden="true">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Segurança */}
      <section className="nfse-section reveal">
        <h2 className="nfse-section-title">Segurança de verdade</h2>
        <div className="nfse-security-grid">
          {securityItems.map((item) => (
            <div key={item.text} className="nfse-security-item">
              <div className="nfse-security-icon" aria-hidden="true">{item.icon}</div>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Planos */}
      <section id="nfse-planos" className="nfse-section nfse-section-alt">
        <h2 className="nfse-section-title">Planos</h2>
        <div className="nfse-plans-table-wrapper">
          <table className="nfse-plans-table">
            <thead>
              <tr>
                <th>Plano</th>
                <th>Preço</th>
                <th>Para quem</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.name} className={plan.highlight ? 'nfse-plan-highlight' : ''}>
                  <td>
                    {plan.name}
                    {plan.highlight && <span className="nfse-plan-badge">Mais popular</span>}
                  </td>
                  <td>{plan.price}</td>
                  <td>{plan.target}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="nfse-plans-note">
          Ciclo anual com <strong>17% de desconto</strong>. Troca de plano com ajuste pro-rata
          automático. Cancele quando quiser — o acesso vale até o fim do período pago.
        </p>
        <div className="nfse-plans-cta">
          <a href={NFSE_PLANOS_URL} target="_blank" rel="noopener noreferrer" className="nfse-cta-primary">
            Ver todos os planos <FaArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="nfse-section reveal">
        <h2 className="nfse-section-title">Perguntas rápidas</h2>
        <div className="nfse-faq-list">
          {faqs.map((faq) => (
            <div key={faq.question} className="nfse-faq-item">
              <h3>
                <FaCheck className="nfse-faq-check" aria-hidden="true" /> {faq.question}
              </h3>
              <p>{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="nfse-final-cta reveal reveal--scale">
        <h2>Pronto para emitir sua primeira nota?</h2>
        <p>Crie sua conta em minutos e teste com o seu CNPJ real. É grátis por 14 dias, sem cartão.</p>
        <div className="nfse-hero-buttons">
          <a href={NFSE_URL} target="_blank" rel="noopener noreferrer" className="nfse-cta-invert">
            Começar agora <FaArrowRight aria-hidden="true" />
          </a>
          <a
            href={NFSE_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nfse-cta-ghost"
          >
            Documentação da API
          </a>
        </div>
      </section>
    </div>
  );
};

export default NFSe;
